import { ActionIcon, Button, Checkbox, Group, Menu, TextInput } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconFilter, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import { HiOutlineViewBoards } from 'react-icons/hi';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface ToolbarProps {
  showSearch?: boolean;
  showFilter?: boolean;
  showFilterColumn?: boolean;
  showCreateButton?: boolean;
  searchPlaceholder?: string;
  filterButtonText?: string;
  filterColumnText?: string;
  createButtonText?: string;
  defaultSearchValue?: string;
  buttonCreateLeftSection?: React.ReactNode;
  buttonFilterLeftSection?: React.ReactNode;
  onFilter?: () => void;
  onCreate?: () => void;
  setColumns?: (_data: any) => void;
  columns?: any[];
  additionalButtons?: React.ReactNode;
}

const Toolbar: React.FC<ToolbarProps> = ({
  createButtonText = 'Create',
  filterButtonText = 'Filter',
  filterColumnText = 'Columns',
  onFilter,
  onCreate,
  searchPlaceholder = 'Search...',
  showCreateButton = true,
  showFilter = true,
  showFilterColumn = false,
  showSearch = true,
  defaultSearchValue,
  buttonFilterLeftSection,
  /* eslint-disable */
  buttonCreateLeftSection,
  /* eslint-enable */
  setColumns,
  columns,
  additionalButtons
}) => {
  const location = useLocation();
  const navigation = useNavigate();
  const [opened, { toggle }] = useDisclosure(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [debounced] = useDebouncedValue(searchValue, 1000);

  useEffect(() => {
    if (defaultSearchValue) {
      setSearchValue(defaultSearchValue);
    }
    return () => {
      setSearchValue('');
    };
  }, [defaultSearchValue]);

  useEffect(() => {
    const pathname = location?.pathname;
    navigation(`${pathname}?search=${debounced}`);
  }, [debounced]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleClearSearch = () => {
    navigation(location?.pathname);
    setSearchValue('');
  };

  const handleColumn = (checked: boolean, column: any) => {
    const _columns = columns?.map((col) => {
      if (col.props === column.props) {
        col.active = checked;
      }
      return col;
    });
    if (setColumns) {
      setColumns(_columns);
    }
  };

  return (
    <Group justify='space-between'>
      <Group>
        {showSearch && (
          <TextInput
            placeholder={searchPlaceholder}
            rightSection={
              searchValue ? (
                <ActionIcon title='Clear search' variant='transparent' onClick={handleClearSearch}>
                  <IconX />
                </ActionIcon>
              ) : (
                <IconSearch />
              )
            }
            onChange={(e) => handleSearch(e)}
            value={searchValue}
          />
        )}
        {showFilter && (
          <Button
            leftSection={buttonFilterLeftSection || <IconFilter stroke={1.2} />}
            variant='default'
            onClick={onFilter}
          >
            {filterButtonText}
          </Button>
        )}
        {showFilterColumn && (
          <>
            <Menu opened={opened} closeOnClickOutside onChange={() => toggle()}>
              <Menu.Target>
                <Button
                  leftSection={<HiOutlineViewBoards size={20} />}
                  variant='default'
                  type='button'
                  onClick={() => toggle()}
                >
                  {filterColumnText}
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                {columns?.map((item, index) => (
                  <Menu.Item
                    key={index}
                    style={{ cursor: 'default' }}
                    disabled={item.heading === 'Action'}
                    leftSection={
                      item.heading !== 'Action' && (
                        <Checkbox
                          disabled={item.disabled}
                          onChange={(event) => handleColumn(event.currentTarget.checked, item)}
                          checked={item.active}
                        />
                      )
                    }
                  >
                    {item.heading}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </>
        )}
      </Group>

      {(showCreateButton || additionalButtons) && (
        <Group justify='flex-end' gap='xs'>
          {additionalButtons}
          {showCreateButton && (
            <Button leftSection={<IconPlus />} onClick={onCreate}>
              {createButtonText}
            </Button>
          )}
        </Group>
      )}
    </Group>
  );
};

export default Toolbar;
